import React, { Component } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import './element-charts-infographics.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

export default class ElementChartsInfographics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
      fallbackTableExpanded: false
    };
  }

  onToggleFallbackTable = () => {
    this.setState({
      fallbackTableExpanded: !this.state.fallbackTableExpanded
    });
  };

  getDefaultColors() {
    return [
      'rgba(45, 123, 69, 0.6)',
      'rgba(255, 99, 132, 0.6)',
      'rgba(54, 162, 235, 0.6)',
      'rgba(255, 205, 86, 0.6)',
      'rgba(75, 192, 192, 0.6)',
      'rgba(153, 102, 255, 0.6)',
      'rgba(255, 159, 64, 0.6)'
    ];
  }

  getChartData() {
    const { content } = this.props;
    const { chart_data = { labels: [], datasets: [] }, label_colors = [] } = content;
    const defaultColors = this.getDefaultColors();
    
    const datasets = chart_data.datasets.map((dataset, datasetIndex) => {
      if (content.chart_type === 'pie' || content.chart_type === 'doughnut') {
        // Para pizza/rosca: mapear cores das labels para cada fatia
        const backgroundColors = [];
        const borderColors = [];
        
        for (let i = 0; i < chart_data.labels.length; i++) {
          const color = (label_colors && label_colors[i]) || defaultColors[i % defaultColors.length];
          backgroundColors.push(color);
          borderColors.push(color);
        }
        
        return {
          ...dataset,
          backgroundColor: backgroundColors,
          borderColor: borderColors
        };
      } else {
        // Para barras/linhas: usar cor da série ou cor padrão
        const seriesColor = dataset.backgroundColor || defaultColors[datasetIndex % defaultColors.length];
        return {
          ...dataset,
          backgroundColor: seriesColor,
          borderColor: dataset.borderColor || seriesColor
        };
      }
    });
    
    return {
      labels: chart_data.labels || [],
      datasets
    };
  }

  getChartOptions() {
    const { content } = this.props;
    const { chart_options = {} } = content;
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: chart_options.legend_position || 'top',
          display: chart_options.show_legend !== false
        },
        title: {
          display: !!chart_options.chart_title,
          text: chart_options.chart_title || ''
        },
        tooltip: {
          enabled: chart_options.show_tooltips !== false
        }
      },
      scales: content.chart_type === 'pie' || content.chart_type === 'doughnut' ? {} : {
        y: {
          beginAtZero: true,
          display: chart_options.show_y_axis !== false,
          title: {
            display: !!chart_options.y_axis_label,
            text: chart_options.y_axis_label || ''
          }
        },
        x: {
          display: chart_options.show_x_axis !== false,
          title: {
            display: !!chart_options.x_axis_label,
            text: chart_options.x_axis_label || ''
          }
        }
      },
      ...chart_options
    };
  }

  renderChart() {
    const { content } = this.props;
    const { chart_type = 'bar' } = content;
    const data = this.getChartData();
    const options = this.getChartOptions();

    const chartHeight = content.chart_height || 400;

    const chartKey = content._chartKey || 'default';

    switch (chart_type) {
      case 'line':
        return <Line key={chartKey} data={data} options={options} height={chartHeight} />;
      case 'pie':
        return <Pie key={chartKey} data={data} options={options} height={chartHeight} />;
      case 'doughnut':
        return <Doughnut key={chartKey} data={data} options={options} height={chartHeight} />;
      case 'bar':
      default:
        return <Bar key={chartKey} data={data} options={options} height={chartHeight} />;
    }
  }



  renderFallbackTable() {
    const { content } = this.props;
    const { chart_data = { labels: [], datasets: [] } } = content;

    if (!chart_data.labels.length || !chart_data.datasets.length) {
      return null;
    }

    return (
      <div className="rdp-element-charts-fallback-table" role="table" aria-label="Dados do gráfico em formato de tabela">
        <table>
          <thead>
            <tr>
              <th scope="col">Categoria</th>
              {chart_data.datasets.map((dataset, index) => (
                <th key={index} scope="col">{dataset.label || `Série ${index + 1}`}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {chart_data.labels.map((label, labelIndex) => (
              <tr key={labelIndex}>
                <th scope="row">{label}</th>
                {chart_data.datasets.map((dataset, datasetIndex) => (
                  <td key={datasetIndex}>
                    {dataset.data[labelIndex] || 0}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { content, headerActionsTemplate, footerActionsTemplate, className } = this.props;

    const containerStyle = {
      backgroundColor: content.background_color || '#FFFFFF',
      paddingTop: `${content.padding_top || 35}px`,
      paddingBottom: `${content.padding_bottom || 35}px`
    };

    return (
      <div className={`rdp-element-charts-infographics ${className || ''}`} style={containerStyle}>
        {headerActionsTemplate}
        
        <div className="rdp-element-charts-content">
          {content.content_title && (
            <h2 className="rdp-element-charts-title">{content.content_title}</h2>
          )}
          
          <div 
            className="rdp-element-charts-visualization"
            style={{ height: `${content.chart_height || 400}px` }}
            role="img"
            aria-label={content.accessibility_description || `Gráfico: ${content.content_title || 'Sem título'}`}
          >
            {this.renderChart()}
          </div>

          {content.show_fallback_table && (
            <div className={`rdp-element-charts-fallback-details ${this.state.fallbackTableExpanded ? 'expanded' : 'not-expanded'}`}>
              <span className="rdp-element-charts-fallback-title" onClick={this.onToggleFallbackTable}>
                Ver dados em formato de tabela
              </span>
              <div className="rdp-element-charts-fallback-content">
                {this.renderFallbackTable()}
              </div>
            </div>
          )}

          {content.description && (
            <div className="rdp-element-charts-description">
              <div dangerouslySetInnerHTML={{ __html: content.description }} />
            </div>
          )}
        </div>

        {footerActionsTemplate}
      </div>
    );
  }
}